<template lang="pug">
.dropdown(
  @keydown="handleKeydown"
  :class="{'is-active' : menuVisible}"
  :style="{zIndex}")
  .dropdown__trigger(
    @keydown.enter.prevent="toggleDropdown"
    @click.prevent="toggleDropdown"
    role="button"
    tabindex="0")
    span.dropdown__selected(
      :class="{placeholder: isPlaceholder}"
    ) {{ displayText }}
    button.dropdown__clear(
      @click.self.stop.prevent="clearOption()"
      @keydown.enter.self.stop.prevent="clearOption()"
      v-if="!isPlaceholder"
    )
  .dropdown__menu(ref="menu")
    label.dropdown__option(
      v-for="(option, index) in data.options"
      :key="`${option.name}-${index}`"
      :for="`${option.name}-${index}`"
      tabindex="-1"
    )
      span.dropdown__text {{ option.name }}
      input.dropdown__input(
        v-model="value"
        ref="options"
        :type="multi ? 'checkbox' : 'radio'"
        :id="`${option.name}-${index}`"
        :value="option"
      )
</template>

<script>
export default {
  name: "Dropdown",
  props: ["data", "multi", "index", "total"],
  data() {
    return {
      value: this.multi ? [] : null,
      menuVisible: false,
      focusIndex: -1
    };
  },
  computed: {
    zIndex() {
      return this.total + 3 - this.index;
    },
    isPlaceholder() {
      return this.multi ? this.value.length === 0 : !this.value;
    },
    displayText() {
      if (this.isPlaceholder) {
        return this.data.placeholder;
      }
      if (this.multi) {
        return `${this.value.length} Selected`;
      }
      return this.value.name;
    }
  },
  methods: {
    toggleDropdown() {
      this.menuVisible = !this.menuVisible;
      this.$refs.menu.scrollTo(0, 0);
    },
    closeDropdown(e) {
      if (!this.$el.contains(e.target)) {
        this.menuVisible = false;
      }
    },
    clearOption() {
      this.menuVisible = false;
      if (this.multi) {
        this.value = [];
      } else {
        this.value = null;
      }
      this.$emit("clear-option", this.data.reference);
    },
    selectOption() {
      if (!this.multi) {
        this.menuVisible = false;
      }
      this.$emit("select-option", [this.data.reference, this.value]);
    },
    handleKeydown(e) {
      if (!this.menuVisible) return;
      e.preventDefault();
      e.stopPropagation();

      switch (e.key) {
        case "Tab":
          if (e.shiftKey) {
            this.focusIndex = (this.focusIndex - 1 + this.data.options.length) % this.data.options.length;
          } else {
            this.focusIndex = (this.focusIndex + 1) % this.data.options.length;
          }
          this.$refs.options[this.focusIndex].focus();
          break;
        case "ArrowDown":
          this.focusIndex = (this.focusIndex + 1) % this.data.options.length;
          this.$refs.options[this.focusIndex].focus();
          break;
        case "ArrowUp":
          this.focusIndex = (this.focusIndex - 1 + this.data.options.length) % this.data.options.length;
          this.$refs.options[this.focusIndex].focus();
          break;
        case "Enter":
        case " ":
          this.$refs.options[this.focusIndex].click();
          break;
        case "Escape":
          this.focusIndex = -1;
          this.menuVisible = false;
          break;
      }
    }
  },
  watch: {
    value() {
      this.selectOption();
    }
  },
  mounted() {
    document.addEventListener("click", this.closeDropdown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeDropdown);
  }
};
</script>

<style lang="scss" scoped>
.dropdown {
  display: flex;
  flex-direction: column;
  height: 3rem;
  width: 100%;
  background: $white;
  border: 1px solid #6C6F70;
  margin: auto 0;
  position: relative;

  @include mq(lg) {
    grid-area: span 1 / span 1;
  }

  &__trigger {
    all: unset;
    appearance: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1 1 100%;
    position: relative;
    z-index: 2;
    cursor: pointer;
    padding: 0 1.75rem 0 0.5rem;
    background: $white;
    font-size: 1.125rem;
    line-height: 1;

    &:after {
      @include icon;
      content: "\e90a";
      position: absolute;
      right: 0.5rem;
      top: 50%;
      font-size: 0.75rem;
      transform: translateY(-50%);
    }
  }

  &__selected {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 1 1 100%;

    &.placeholder {
      opacity: .6;
    }
  }

  &__clear {
    all: unset;
    appearance: none;
    flex: 0 0 auto;
    cursor: pointer;
    z-index: 2;
    line-height: 1;
    font-size: 0.6825rem;
    color: red;

    &::before {
      @include icon;
      content: "\e90e";
    }
  }

  &__menu {
    display: flex;
    flex-direction: column;
    position: absolute;
    z-index: 1;
    top: calc(100% - 1rem);
    left: -1px;
    right: -1px;
    height: auto;
    max-height: 300px;
    overflow-y: auto;
    background: $white;
    border: 1px solid #6D7A86;
    box-shadow: 0 0.25rem 1rem -0.25rem rgba(0, 0, 0, 0.5);
    transition: 0.15s ease;
    visibility: hidden;
    opacity: 0;
    pointer-events: none;

    .is-active & {
      visibility: visible;
      opacity: 1;
      pointer-events: all;
      top: calc(100% - 1px);
    }
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: .5rem 1rem;
    font-size: 1rem;
    cursor: pointer;
    transition: 0.15s ease;
    color: $darkGray;
    position: relative;

    &:focus-within {
      background: $teal !important;
      color: $white;
    }

    &:hover {
      background: #efefef;
    }

    &:has(input[type="checkbox"]) {
      &::before {
        content: '\2713';
        display: flex;
        align-items: center;
        justify-content: center;
        height: 1.125rem;
        width: 1.125rem;
        border: 1px solid $darkGray;
        background: $white;
        color: transparent;
        font-size: 0.75rem;
      }
    }

    &:has(input[type="checkbox"]:checked) {
      &::before {
        color: $teal;
      }
    }
  }

  &__input {
    all: unset;
    appearance: none;
    position: absolute;
    top: 0;
    left: 0;
    height: 1px;
    width: 1px;
    opacity: 0;
    pointer-events: none;
  }
}
</style>
